import { useState, useEffect, useMemo, useCallback } from 'react';
import { useStore, useDispatch } from 'react-redux';

import { fitbitToOsPermissionMap } from 'config';
import { OsPermissionStatus } from '../../constants';
import { enableOsPermission as enableOsPermissionAction } from 'actions/gallery';
import { translations } from 'i18n';

const filterOnlyVisible = (permissions) => {
  const {
    comms: { permissions: permissionsText }
  } = translations;

  return Object.keys(permissions).filter(
    (permission) => !!permissionsText[permission]
  );
};

const useCheckOsPermissions = (app, onEnableOsPermission = () => {}) => {
  const dispatch = useDispatch();
  const store = useStore();

  const enableOsPermission = async (permissionName) => {
    await dispatch(
      enableOsPermissionAction(permissionName, {
        appName: app.name
      })
    );
    onEnableOsPermission();
  };

  const checkOsPermission = async (permissionName, newValue) => {
    const osPermissionName = fitbitToOsPermissionMap[permissionName];
    if (!osPermissionName) {
      return newValue;
    }

    if (newValue && app.osPermissionsEnableableMap[permissionName]) {
      await enableOsPermission(osPermissionName);

      const {
        gallery: { osPermissions: updatedOsPermissions }
      } = store.getState();
      if (
        updatedOsPermissions[osPermissionName] === OsPermissionStatus.DENIED
      ) {
        return false;
      }
    }

    return newValue;
  };

  return async (permissions) => {
    const updated = {};

    for (const permission in permissions) {
      updated[permission] = await checkOsPermission(
        permission,
        permissions[permission]
      );
    }

    return updated;
  };
};

export const useAbstractEditor = ({
  app,
  permissions,
  onEnableOsPermission,
  onChange = () => {}
}) => {
  const checkPermissions = useCheckOsPermissions(app, onEnableOsPermission);
  const [localPermissions, setLocalPermissions] = useState(permissions);
  const visiblePermissions = useMemo(
    () => filterOnlyVisible(localPermissions),
    [localPermissions]
  );

  useEffect(() => setLocalPermissions(permissions), [
    permissions,
    setLocalPermissions
  ]);

  const setPermission = useCallback(
    async (permissionName, newValue) => {
      const oldValue = localPermissions[permissionName];
      if (newValue === true) {
        const { [permissionName]: checkedValue } = await checkPermissions({
          [permissionName]: newValue
        });
        newValue = checkedValue;
      }

      if (newValue !== oldValue) {
        setLocalPermissions(() => {
          const updatedPermissions = {
            ...localPermissions,
            [permissionName]: newValue
          };
          onChange(updatedPermissions);
          return updatedPermissions;
        });
      }
    },
    [localPermissions, setLocalPermissions, checkPermissions, onChange]
  );

  const setAllPermissions = useCallback(
    async (newValue) => {
      let updatedPermissions = { ...localPermissions };
      for (const name of visiblePermissions) {
        updatedPermissions[name] = newValue;
      }
      if (newValue === true) {
        updatedPermissions = await checkPermissions(updatedPermissions);
      }
      setLocalPermissions(updatedPermissions);
      onChange(updatedPermissions);
    },
    [
      localPermissions,
      visiblePermissions,
      setLocalPermissions,
      checkPermissions,
      onChange
    ]
  );

  return {
    permissions: localPermissions,
    visiblePermissions: filterOnlyVisible(localPermissions),
    setPermission,
    setAllPermissions
  };
};
